import React, {
    forwardRef,
    memo,
    ReactElement,
    useEffect,
    useImperativeHandle,
    useRef,
} from "react";
import { UserVideoType } from ".";
import { Avatar } from "antd";
import { WifiIcon } from "../SvgIconCom/ChatBottomToolIcon";

export interface VideoWindowItemProps {
    member: UserVideoType;
    className?: string;
    canShowVideo?: boolean;
    videoContainer: HTMLDivElement | null;
    handleMemberCameraOff?: () => void;
    handleDoubleClick?: (member: UserVideoType) => void;
}

export const VideoWindowItem = memo(
    forwardRef<{ divRef: HTMLDivElement; videoRef: HTMLDivElement }, VideoWindowItemProps>(
        (
            {
                member,
                className,
                canShowVideo = true,
                videoContainer,
                handleMemberCameraOff,
                handleDoubleClick,
            },
            ref,
        ): ReactElement => {
            const videoContainerRef = useRef(videoContainer);
            const memberCamOffRef = useRef(handleMemberCameraOff);
            useEffect(() => {
                if (member.user.camera) {
                    if (videoContainerRef.current && videoRef.current) {
                        videoRef.current.appendChild(videoContainerRef.current);
                    }
                } else {
                    memberCamOffRef.current?.();
                    if (videoRef.current && videoRef.current.innerHTML.length) {
                        videoRef.current.innerHTML = "";
                    }
                }
            }, [member.user.camera, member.user.name]);
            useEffect(() => {
                videoContainerRef.current = videoContainer;
            }, [videoContainer]);
            useEffect(() => {
                memberCamOffRef.current = handleMemberCameraOff;
            }, [handleMemberCameraOff]);
            const divRef = useRef<HTMLDivElement>(null);
            const videoRef = useRef<HTMLDivElement>(null);
            useImperativeHandle(
                ref,
                () => ({
                    divRef: divRef.current as HTMLDivElement,
                    videoRef: videoRef.current as HTMLDivElement,
                }),
                [],
            );
            return (
                <div
                    key={member.user.userUUID}
                    className={className}
                    onDoubleClick={() => handleDoubleClick?.(member)}
                    onDragOver={e => {
                        e.preventDefault();
                    }}
                    onDrop={e => {
                        e.preventDefault();
                    }}
                >
                    <div
                        ref={divRef}
                        className="video-special-content"
                        data-name={member.user.name}
                        data-uid={member.user.userUUID}
                    ></div>
                    <div ref={videoRef} className="video-show-content"></div>
                    {!member.user.camera && canShowVideo && (
                        <div className="default-user-avatar">
                            <div className="avatar">
                                <Avatar src={member.user.avatar} />
                            </div>
                        </div>
                    )}
                    <span className="overlay-info">
                        <Avatar size={32} src={member.user.avatar} />
                        <span className="overlay-name">{member.user.name}</span>
                        <WifiIcon
                            className="overlay-icon"
                            delay={member.user.networkQuality?.delay}
                        />
                    </span>
                </div>
            );
        },
    ),
    // Only re-render if specific props change
    (prevProps, nextProps) =>
        prevProps.member.user.userUUID === nextProps.member.user.userUUID &&
        prevProps.member.user.camera === nextProps.member.user.camera &&
        prevProps.member.user.isListen === nextProps.member.user.isListen &&
        prevProps.member.user.mic === nextProps.member.user.mic &&
        prevProps.member.user.isSpeak === nextProps.member.user.isSpeak &&
        prevProps.member.user.avatar === nextProps.member.user.avatar &&
        prevProps.member.user.name === nextProps.member.user.name &&
        prevProps.member.user.networkQuality?.delay ===
            nextProps.member.user.networkQuality?.delay &&
        prevProps.className === nextProps.className &&
        prevProps.canShowVideo === nextProps.canShowVideo,
);

VideoWindowItem.displayName = "VideoWindowItem";
